import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Clock, FileText, Trash2, ArrowRight, FileCheck, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function isLockExpired(expiresAt) {
  if (!expiresAt) return true;
  return new Date() > new Date(expiresAt);
}

function timeUntilExpiry(expiresAt) {
  if (!expiresAt) return 'expired';
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'expired';
  const mins = Math.floor(diff / 60000);
  return `${mins}m left`;
}

const STATUS_STYLES = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  slot_selected: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  pending_order_creation: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

export function PendingDraftsPanel({ api, onContinue, onCompleteOrder, refreshKey }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDrafts = useCallback(async () => {
    try {
      const resp = await api.get('/schedule/drafts/pending');
      setDrafts(resp.data.drafts || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts, refreshKey]);

  const handleDiscard = async (draftId) => {
    try {
      await api.delete(`/schedule/drafts/${draftId}`);
      toast.success('Draft discarded');
      fetchDrafts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to discard');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (drafts.length === 0) return null;

  return (
    <Card data-testid="pending-drafts-panel">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-500" />
          Pending Drafts ({drafts.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-2">
        {drafts.map(d => {
          const expired = d.status === 'slot_selected' && isLockExpired(d.lock_expires_at);
          return (
            <div key={d.id}
              className={cn(
                'p-3 rounded-lg border transition-all',
                expired ? 'border-red-500/30 bg-red-500/5' : 'border-border',
              )}
              data-testid={`pending-draft-${d.id}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="font-medium text-sm truncate max-w-[180px] sm:max-w-none">{d.title}</span>
                <Badge variant="outline" className={cn('text-[10px] whitespace-nowrap', STATUS_STYLES[d.status])}>
                  {d.status?.replace(/_/g, ' ')}
                </Badge>
              </div>

              {/* Slot info */}
              {d.locked_date && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground mb-2">
                  <span>{formatDate(d.locked_date)}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {d.locked_time_start} - {d.locked_time_end}
                  </span>
                  {d.lock_expires_at && (
                    <span className={cn(
                      'flex items-center gap-1',
                      expired ? 'text-red-400' : 'text-amber-400'
                    )}>
                      {expired ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      {timeUntilExpiry(d.lock_expires_at)}
                    </span>
                  )}
                </div>
              )}

              {d.status === 'draft' && !d.locked_date && (
                <p className="text-xs text-muted-foreground mb-2">No slot selected yet</p>
              )}

              {/* Actions - responsive */}
              <div className="flex flex-wrap gap-2">
                {d.status === 'draft' && (
                  <Button size="sm" variant="outline" className="text-xs h-8 sm:h-7 px-3"
                    onClick={() => onContinue?.(d)} data-testid={`draft-continue-${d.id}`}>
                    <ArrowRight className="h-3 w-3 mr-1" />Continue
                  </Button>
                )}
                {d.status === 'slot_selected' && !expired && (
                  <Button size="sm" className="text-xs h-8 sm:h-7 px-3 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => onCompleteOrder?.(d)} data-testid={`draft-complete-${d.id}`}>
                    <FileCheck className="h-3 w-3 mr-1" />Complete
                  </Button>
                )}
                {d.status === 'slot_selected' && expired && (
                  <Badge variant="outline" className="text-[10px] text-red-400 border-red-400/30">
                    Lock expired
                  </Badge>
                )}
                <Button size="sm" variant="ghost" className="text-xs h-8 sm:h-7 px-2 text-red-400 hover:text-red-300 ml-auto"
                  onClick={() => handleDiscard(d.id)} data-testid={`draft-discard-${d.id}`}>
                  <Trash2 className="h-3 w-3" />
                  <span className="hidden sm:inline ml-1">Discard</span>
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
